import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { guardRoute } from "@/lib/permissions";
import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { dataProvider as supabase } from "@/lib/providers/data-provider";
import { fetchMyPortalContext } from "@/lib/portal/portal-context-service";
import { SupplierPortalWorkspace } from "@/components/portal/SupplierPortalWorkspace";
import { PortalAuthGate } from "@/components/portal/PortalAuthGate";
import type { SupplierErpPurchase } from "@/lib/portal/supplier-erp-mapper";
import { getPortalHubPreferences } from "@/lib/customization-hub-preferences-store";

type SupplierPortalData = {
  profile: {
    id: string;
    full_name: string;
    phone: string | null;
    email: string | null;
    village_city?: string | null;
    current_address?: string | null;
  };
  purchases: SupplierErpPurchase[];
  outside_work: Array<{ id: string; order_id?: string | null; created_at: string; data?: unknown }>;
};

export const Route = createFileRoute("/supplier-portal")({
  beforeLoad: ({ location }) => guardRoute(location.pathname),
  head: () => ({
    meta: [{ title: "Supplier Portal · AVS Gold ERP" }],
  }),
  component: SupplierPortal,
});

function SupplierPortal() {
  return (
    <PortalAuthGate
      portalType="supplier"
      loginAudience="supplier"
      returnPath="/supplier-portal"
    >
      <SupplierPortalInner />
    </PortalAuthGate>
  );
}

function SupplierPortalInner() {
  const navigate = useNavigate();
  const [data, setData] = useState<SupplierPortalData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const portalPrefs = getPortalHubPreferences();

  const loadPortal = useCallback(async () => {
    const portalCtx = await fetchMyPortalContext("supplier");
    if (!portalCtx) {
      setError("Your account is not linked to a supplier portal. Contact your firm administrator.");
      return;
    }

    const { data: result, error: queryError } = await (supabase as any).rpc("get_supplier_portal");
    if (queryError) {
      setError(queryError.message || "Could not load supplier portal data.");
      return;
    }

    setData(result as SupplierPortalData);
    setError(null);
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      await loadPortal();
      if (!active) return;
    })();
    return () => {
      active = false;
    };
  }, [loadPortal, navigate]);

  if (error) {
    return (
      <main className="mx-auto max-w-lg p-6 text-center space-y-4">
        <p className="text-sm text-destructive">{error}</p>
        <button
          type="button"
          className="text-sm text-gold underline"
          onClick={() => void supabase.auth.signOut().then(() => navigate({ to: "/login" }))}
        >
          Sign out
        </button>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="grid min-h-[50vh] place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-gold" aria-label="Loading supplier portal" />
      </main>
    );
  }

  return (
    <SupplierPortalWorkspace
      supplierId={data.profile.id}
      supplierName={data.profile.full_name}
      erpPurchases={data.purchases ?? []}
      erpOutsideWork={data.outside_work ?? []}
      onRefresh={loadPortal}
      portalPrefs={{
        supplierPo: portalPrefs.supplierPo,
        supplierGoldSettlements: portalPrefs.supplierGoldSettlements,
      }}
      onSignOut={() => {
        void supabase.auth.signOut().then(() => {
          window.location.href = "/login?audience=supplier";
        });
      }}
    />
  );
}
